// theme-bundle.js - Combined core theme scripts for better performance

// constants.js
const ON_CHANGE_DEBOUNCE_TIMER = 300;
const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

// pubsub.js
let subscribers = {};
function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }
  subscribers[eventName] = [...subscribers[eventName], callback];
  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}
function publish(eventName, data) {
  if (subscribers[eventName]) {
    const promises = subscribers[eventName].map((callback) => callback(data))
    return Promise.all(promises);
  } else {
    return Promise.resolve()
  }
}

// details-disclosure.js
class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;
    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }
  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }
  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
      setTimeout(() => {
        this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
      }, 150);
    } else {
      this.animations.forEach((animation) => animation.cancel());
      this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
    }
  }
  close() {
    this.mainDetailsToggle.removeAttribute('open');
  }
}
customElements.define('details-disclosure', DetailsDisclosure);
class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }
  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;
    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    
    window.requestAnimationFrame(() => {
      const bottom = Math.floor(this.header.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty(
        '--header-bottom-position-desktop',
        `${bottom}px`
      );
    });
  }
}
customElements.define('header-menu', HeaderMenu);

// details-modal.js
class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    const closeButton = this.querySelector('button[type="button"]');
    if (closeButton) closeButton.addEventListener('click', this.close.bind(this));
    this.summaryToggle.setAttribute('role', 'button');
  }
  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }
  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }
  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }
  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');
    if (typeof trapFocus === 'function') {
      trapFocus(
        this.detailsContainer.querySelector('[tabindex="-1"]'),
        this.detailsContainer.querySelector('input:not([type="hidden"])')
      );
    }
  }
  close(focusToggle = true) {
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(focusToggle ? this.summaryToggle : null);
    }
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }
}
customElements.define('details-modal', DetailsModal);

// search-form.js
class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }
  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }
  onChange() {
    this.toggleResetButton();
  }
  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }
  onFormReset(event) {
    event.preventDefault();
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}
customElements.define('search-form', SearchForm);

// mega-menu-hover.js
document.addEventListener('DOMContentLoaded', function() {
  const DESKTOP_BP = 990;
  let initialized = false;
  let listeners = [];
  function teardown() {
    listeners.forEach(({ target, type, handler }) => target.removeEventListener(type, handler));
    listeners = [];
    initialized = false;
  }
  function on(target, type, handler) {
    target.addEventListener(type, handler);
    listeners.push({ target, type, handler });
  }
  function setup() {
    if (initialized) return;
    const menuItems = document.querySelectorAll('.header__inline-menu > ul > li');
    if (!menuItems.length) return;
    menuItems.forEach(function(item) {
      const details = item.querySelector('.mega-menu');
      const megaMenuContent = item.querySelector('.mega-menu__content');
      if (!details || !megaMenuContent) return;
      let closeTimeout;
      const openMenu = () => {
        clearTimeout(closeTimeout);
        menuItems.forEach(function(otherItem) {
          if (otherItem !== item) {
            const otherDetails = otherItem.querySelector('.mega-menu');
            if (otherDetails) otherDetails.removeAttribute('open');
          }
        });
        
        if (!details.hasAttribute('open')) {
          details.setAttribute('open', '');
        }
      };
      const scheduleClose = () => {
        clearTimeout(closeTimeout);
        closeTimeout = setTimeout(() => {
          if (!item.matches(':hover') && !megaMenuContent.matches(':hover')) {
            details.removeAttribute('open');
          }
        }, 300);
      };
      on(item, 'mouseenter', openMenu);
      on(item, 'mouseleave', scheduleClose);
      on(megaMenuContent, 'mouseenter', openMenu);
      on(megaMenuContent, 'mouseleave', scheduleClose);
    });
    initialized = true;
  }
  function maybeInit() {
    if (window.innerWidth >= DESKTOP_BP) {
      setup();
    } else if (initialized) {
      document.querySelectorAll('.mega-menu[open]').forEach(d => d.removeAttribute('open'));
      teardown();
    }
  }
  maybeInit();
  window.addEventListener('resize', () => {
    clearTimeout(window.__megaMenuResizeTimer);
    window.__megaMenuResizeTimer = setTimeout(maybeInit, 150);
  });
});

+(function() {
  if (typeof window !== 'undefined' && !window.__gobSilenceLogs) {
    window.__gobSilenceLogs = true;
    const noop = () => {};
    ['log', 'info', 'debug'].forEach((m) => {
      try {
        if (console[m]) console[m] = noop;
      } catch (e) {}
    });
  }
})();

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      // Accordion behaviour: rely on native <details> open/close instead of sliding submenu
      // Wait a tick so the browser toggles the `open` attribute, then sync aria-expanded
      setTimeout(() => {
        const isNowOpen = detailsElement.hasAttribute('open');
        summaryElement.setAttribute('aria-expanded', isNowOpen);
        if (isNowOpen) {
          trapFocus(detailsElement, summaryElement);
        } else {
          removeTrapFocus(summaryElement);
        }
      }, 10);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    // Delay aria-expanded="true" to allow menu to open completely
    setTimeout(() => {
      summaryElement.setAttribute('aria-expanded', true);
    }, 150);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    // Always set aria-expanded to false when closing, regardless of event type
    if (elementToFocus) elementToFocus.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || (this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0);
    
    window.requestAnimationFrame(() => {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    });
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    // Delay aria-expanded="true" to allow menu to open completely
    setTimeout(() => {
      summaryElement.setAttribute('aria-expanded', true);
    }, 150);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = debounce(() => {
    if (!this.header) return;
    window.requestAnimationFrame(() => {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    });
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  }, 100);
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/19983c63-725a-432e-8315-9e68f262acca',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'A',location:'theme-main.js:SliderComponent:ctor',message:'slider component init',data:{hasSlider:!!this.slider,hasNext:!!this.nextButton,itemsCount:this.sliderItems?.length||0,sliderWidth:this.slider?.clientWidth||0},timestamp:Date.now()})}).catch(()=>{});
    // #endregion agent log

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/19983c63-725a-432e-8315-9e68f262acca',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'A',location:'theme-main.js:SliderComponent:initPages',message:'initPages widths',data:{itemsToShow:this.sliderItemsToShow.length,itemsTotal:this.sliderItems?.length||0,sliderWidth:this.slider?.clientWidth||0,firstItemWidth:this.sliderItemsToShow[0]?.clientWidth||0},timestamp:Date.now()})}).catch(()=>{});
    // #endregion agent log
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/19983c63-725a-432e-8315-9e68f262acca',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'C',location:'theme-main.js:SliderComponent:initPages',message:'initPages offsets',data:{itemOffset:this.sliderItemOffset,slidesPerPage:this.slidesPerPage,totalPages:this.totalPages,scrollLeft:this.slider?.scrollLeft||0},timestamp:Date.now()})}).catch(()=>{});
    // #endregion agent log
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/19983c63-725a-432e-8315-9e68f262acca',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'B',location:'theme-main.js:SliderComponent:update',message:'slider update',data:{scrollLeft:this.slider.scrollLeft,itemOffset:this.sliderItemOffset,currentPage:this.currentPage,totalPages:this.totalPages},timestamp:Date.now()})}).catch(()=>{});
    // #endregion agent log

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/19983c63-725a-432e-8315-9e68f262acca',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'B',location:'theme-main.js:SliderComponent:onButtonClick',message:'button click',data:{name:event.currentTarget.name,step:Number(step),scrollLeft:this.slider.scrollLeft,itemOffset:this.sliderItemOffset,target:this.slideScrollPosition},timestamp:Date.now()})}).catch(()=>{});
    // #endregion agent log
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

// --- Product Description H5 Accordion ---
function convertH5ToAccordions(scope = document) {
  const productDescriptions = scope.querySelectorAll('.product__description, .product-description, #ProductDescription');

  // True when nodes carry meaningful content (any non-whitespace text,
  // or a media element). Empty <p>&nbsp;</p>, stray <br>, etc. → false.
  function nodesHaveContent(nodes) {
    if (!nodes || !nodes.length) return false;
    for (const n of nodes) {
      if (n.nodeType === 1) {
        if ((n.textContent || '').trim().length > 0) return true;
        if (n.querySelector && n.querySelector('img, video, iframe, picture, svg, source, audio')) return true;
      } else if (n.nodeType === 3) {
        if ((n.textContent || '').trim().length > 0) return true;
      }
    }
    return false;
  }

  // Helper to create accordion items consistently. Returns null when
  // there's nothing meaningful to put in the body so the caller can
  // skip appending an empty section.
  function createAccordionItem(title, nodes, shouldNormalize = false) {
    const cleanTitle = (title || '').toString().trim();
    if (!cleanTitle) return null;
    if (!nodesHaveContent(nodes)) return null;

    const accordionItem = document.createElement('details');
    accordionItem.className = 'simple-faq__item';

    const summary = document.createElement('summary');
    summary.className = 'simple-faq__summary';
    summary.innerHTML = `
      <span class="simple-faq__question">${cleanTitle}</span>
      <svg class="simple-faq__icon" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
      </svg>`;

    const contentDiv = document.createElement('div');
    contentDiv.className = 'simple-faq__content rte';
    nodes.forEach(node => contentDiv.appendChild(node));

    accordionItem.appendChild(summary);
    accordionItem.appendChild(contentDiv);
    // Normalize tables only for description-sourced content
    if (shouldNormalize) {
      normalizeTables(contentDiv);
    }
    return accordionItem;
  }

  // Normalize tables: remove hardcoded sizes and convert first-row headers to data cells
  function normalizeTables(root) {
    if (!root || !root.querySelectorAll) return;
    const tables = root.querySelectorAll('table');
    tables.forEach(function(table) {
      // remove width/height attributes and inline styles from table structure elements
      const structuralNodes = table.querySelectorAll('table, thead, tbody, tr, th, td');
      structuralNodes.forEach(function(el) {
        if (el.removeAttribute) {
          el.removeAttribute('width');
          el.removeAttribute('height');
          // remove legacy padding attributes on tables
          if (el.tagName === 'TABLE') {
            el.removeAttribute('cellpadding');
            el.removeAttribute('cellspacing');
          }
        }
        if (el.style && el.style.removeProperty) {
          el.style.removeProperty('width');
          el.style.removeProperty('height');
          el.style.removeProperty('max-width');
          el.style.removeProperty('max-height');
          el.style.removeProperty('min-width');
          el.style.removeProperty('min-height');
          // remove any hard-coded padding
          el.style.removeProperty('padding');
          el.style.removeProperty('padding-left');
          el.style.removeProperty('padding-right');
          el.style.removeProperty('padding-top');
          el.style.removeProperty('padding-bottom');
        }
      });

      // promote first row of data cells to thead > th
      const firstRow = table.querySelector('tr');
      if (firstRow && !firstRow.closest('thead')) {
        const rowCells = Array.from(firstRow.children || []);
        const hasTd = rowCells.some(function(el) { return el.tagName === 'TD'; });
        if (hasTd) {
          // ensure a thead exists and is the first section
          let thead = table.querySelector('thead');
          if (!thead) {
            thead = document.createElement('thead');
            table.insertBefore(thead, table.firstChild);
          }

          const headerTr = document.createElement('tr');
          rowCells.forEach(function(cell) {
            if (cell.nodeType !== 1) return;
            let th;
            if (cell.tagName === 'TD') {
              th = document.createElement('th');
              th.innerHTML = cell.innerHTML;
              // copy useful attributes (class, colspan, rowspan, data-*)
              Array.from(cell.attributes || []).forEach(function(attr) {
                const name = (attr.name || '').toLowerCase();
                if (name === 'width' || name === 'height' || name === 'style') return;
                th.setAttribute(attr.name, attr.value);
              });
            } else if (cell.tagName === 'TH') {
              th = cell.cloneNode(true);
            }
            if (th) {
              // sanitize sizing
              if (th.removeAttribute) {
                th.removeAttribute('width');
                th.removeAttribute('height');
              }
              if (th.style && th.style.removeProperty) {
                th.style.removeProperty('width');
                th.style.removeProperty('height');
                th.style.removeProperty('max-width');
                th.style.removeProperty('max-height');
                th.style.removeProperty('min-width');
                th.style.removeProperty('min-height');
              }
              headerTr.appendChild(th);
            }
          });

          thead.appendChild(headerTr);
          // remove the original first row
          firstRow.parentNode && firstRow.parentNode.removeChild(firstRow);
        }
      }
    });
  }

  productDescriptions.forEach(function(desc) {
    const h5Elements = desc.querySelectorAll('h5');
    // No accordions to build and no extra content – skip early
    if (h5Elements.length === 0 && !desc.textContent.trim()) return;

    // Create accordion container
    const accordionContainer = document.createElement('div');
    accordionContainer.className = 'product-description-accordions';

    /* -----------------------------------------------------------
       SPROUT-TEMPLATE SPECIAL CASE: auto Product Information accordion

       On sprout-template products only:
         - The intro element above (.product-2026-description-intro)
           currently shows ALL leading content (first paragraph
           + any extra paragraphs / lists between it and the
           first <h5>). We want only the first <p> to remain
           visible as the short-description above the accordions.
         - The remaining intro content is moved into a brand-new
           "Product Information" accordion prepended to the
           accordion stack — but only when the editor hasn't
           already authored a literal <h5>Product Information</h5>
           in the description.
    ----------------------------------------------------------- */
    const isSproutTemplate = document.body.classList.contains('sprout-product-template');
    const productInfoH5Exists = Array.from(h5Elements).some(function(h5) {
      return (h5.textContent || '').trim().toLowerCase() === 'product information';
    });
    let sproutProductInfoNodes = null;
    if (isSproutTemplate && !productInfoH5Exists) {
      const introEl = document.querySelector('.product-2026-description-intro');
      if (introEl) {
        const introChildren = Array.from(introEl.childNodes);
        const firstPIdx = introChildren.findIndex(function(n) {
          return n.nodeType === 1 && n.tagName === 'P';
        });
        // Only split when there's at least one <p> AND something after it
        if (firstPIdx >= 0 && introChildren.length > firstPIdx + 1) {
          sproutProductInfoNodes = introChildren
            .slice(firstPIdx + 1)
            .map(function(n) { return n.cloneNode(true); });
          // Trim the intro down to just up-to-and-including the first <p>
          for (let i = introChildren.length - 1; i > firstPIdx; i--) {
            if (introChildren[i].parentNode) {
              introChildren[i].parentNode.removeChild(introChildren[i]);
            }
          }
        }
      }
    }

    /* -----------------------------------------------------------
       1. Drain leading content before the first <h5> tag

       NEVER auto-create a "Product Information" accordion from
       this leading content (except for the sprout-template case
       handled above, which sourced its content from the intro
       element, not from `.product__description`).

       The leading nodes are still removed from `.product__description`
       so the H5-section parser in step 2 starts cleanly at the
       first real heading. They're discarded here because they're
       already rendered above via the description_intro Liquid block
       (or, if that block isn't in block_order, the editor will use
       a real H5 to surface the content instead).
    ----------------------------------------------------------- */
    let leadingCursor = desc.firstChild;
    while (leadingCursor && !(leadingCursor.nodeType === 1 && leadingCursor.tagName === 'H5')) {
      const next = leadingCursor.nextSibling;
      if (leadingCursor.nodeType === 1 || (leadingCursor.nodeType === 3 && leadingCursor.textContent.trim().length)) {
        leadingCursor.remove();
      }
      leadingCursor = next;
    }

    /* Sprout: prepend the salvaged Product Information accordion. */
    if (sproutProductInfoNodes && nodesHaveContent(sproutProductInfoNodes)) {
      const productInfoItem = createAccordionItem('Product Information', sproutProductInfoNodes, true);
      if (productInfoItem) accordionContainer.appendChild(productInfoItem);
    }

    /* -----------------------------------------------------------
       2. Build accordions from remaining <h5> sections
    ----------------------------------------------------------- */
    desc.querySelectorAll('h5').forEach(function(h5) {
      // Skip and remove empty or nearly empty h5 tags (e.g. <h5><br></h5>)
      if (!h5.textContent.trim()) {
        h5.remove();
        return;
      }
      // Collect content nodes until next h5
      const contentNodes = [];
      let nextElement = h5.nextElementSibling;
      while (nextElement && nextElement.tagName !== 'H5') {
        contentNodes.push(nextElement.cloneNode(true));
        const toRemove = nextElement;
        nextElement = nextElement.nextElementSibling;
        toRemove.remove();
      }
      const accordionItem = createAccordionItem(h5.textContent, contentNodes, true);
      if (accordionItem) accordionContainer.appendChild(accordionItem);
      h5.remove();
    });

    /* -----------------------------------------------------------
       3. Append Shipping Information accordion (template controlled)
    ----------------------------------------------------------- */
    const shippingContentElement = document.querySelector('[data-shipping-information]');
    if (shippingContentElement && shippingContentElement.innerHTML.trim()) {
      const shippingNodes = Array.from(shippingContentElement.childNodes).map(n => n.cloneNode(true));
      // Do NOT normalize tables for shipping content
      const shippingItem = createAccordionItem('Shipping Information', shippingNodes, false);
      if (shippingItem) accordionContainer.appendChild(shippingItem);
    }

    // Replace description content with accordion structure
    desc.innerHTML = '';
    // If no items survived empty-content filtering, don't inject an
    // empty container at all.
    if (!accordionContainer.children.length) return;

    // Insert accordion later in the product info container so it groups with other accordions
    const productInfo = desc.closest('.product__info-container');
    if (productInfo) {
      // find last existing accordion (simple-faq__item) or heading
      const lastAccordion = productInfo.querySelector('.simple-faq__item:last-of-type');
      if (lastAccordion && lastAccordion.parentNode) {
        lastAccordion.parentNode.insertBefore(accordionContainer, lastAccordion.nextSibling);
      } else {
        productInfo.appendChild(accordionContainer);
      }
    } else {
      desc.appendChild(accordionContainer);
    }
  });
}

function closeOtherProductAccordions(currentItem) {
  const container = currentItem.closest('.product__info-container');
  if (!container) return;
  container.querySelectorAll('.simple-faq__item[open]').forEach(function(other) {
    if (other === currentItem) return;
    const content = other.querySelector('.simple-faq__content');
    if (!content) return;
    const h = content.offsetHeight + 'px';
    content.style.height = h;
    content.style.overflow = 'hidden';
    content.offsetHeight;
    requestAnimationFrame(function() {
      content.style.transition = 'height 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
      content.style.height = '0px';
    });
    setTimeout(function() {
      other.removeAttribute('open');
      content.style.transition = '';
    }, 400);
  });
}

function sanitizeAccordionTables(root) {
  if (!root || !root.querySelectorAll) return;
  const structuralNodes = root.querySelectorAll('table, thead, tbody, tr, th, td');
  structuralNodes.forEach(function(el) {
    if (el.removeAttribute) {
      el.removeAttribute('width');
      el.removeAttribute('height');
      if (el.tagName === 'TABLE') {
        el.removeAttribute('cellpadding');
        el.removeAttribute('cellspacing');
      }
    }
    if (el.style && el.style.removeProperty) {
      el.style.removeProperty('width');
      el.style.removeProperty('height');
      el.style.removeProperty('max-width');
      el.style.removeProperty('max-height');
      el.style.removeProperty('min-width');
      el.style.removeProperty('min-height');
      el.style.removeProperty('padding');
      el.style.removeProperty('padding-left');
      el.style.removeProperty('padding-right');
      el.style.removeProperty('padding-top');
      el.style.removeProperty('padding-bottom');
    }
  });
}

function initSimpleFaqAccordions(scope = document) {
  const faqItems = scope.querySelectorAll('.simple-faq__item');
  faqItems.forEach(function(item) {
    const summary = item.querySelector('.simple-faq__summary');
    const content = item.querySelector('.simple-faq__content');
    if (!summary || !content) return;
    sanitizeAccordionTables(content);
    
    // Skip if already initialized
    if (item.hasAttribute('data-accordion-initialized')) return;
    item.setAttribute('data-accordion-initialized', 'true');
    
    // Remove any existing event listeners
    const newSummary = summary.cloneNode(true);
    summary.parentNode.replaceChild(newSummary, summary);
    
    // Prevent default details behavior completely
    item.addEventListener('toggle', function(e) {
      e.preventDefault();
      e.stopPropagation();
    });
    
    // Set initial state - always start closed
    item.removeAttribute('open');
    content.style.height = '0px';
    content.style.overflow = 'hidden';
    content.style.transition = '';
    
    // Track animation state
    let isAnimating = false;
    
    // Add our custom click handler
    newSummary.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // Prevent rapid clicking during animation
      if (isAnimating) return;
      
      const isOpen = item.hasAttribute('open');
      isAnimating = true;
      
      if (isOpen) {
        // Close animation (this item only)
        const currentHeight = content.offsetHeight + 'px';
        content.style.height = currentHeight;
        content.style.overflow = 'hidden';
        
        // Force reflow
        content.offsetHeight;
        
        requestAnimationFrame(function() {
          content.style.transition = 'height 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
          content.style.height = '0px';
        });
        
        setTimeout(function() {
          item.removeAttribute('open');
          content.style.transition = '';
          isAnimating = false;
        }, 400);
      } else {
        closeOtherProductAccordions(item);
        // Open animation
        sanitizeAccordionTables(content);
        item.setAttribute('open', '');
        content.style.overflow = 'hidden';
        content.style.transition = '';
        // Must not measure scrollHeight while height:0 + overflow:hidden — mobile
        // stacked tables (display:block tbody/tr in base.css) lay out taller than
        // that constraint reports, so the panel height was too small and overlapped
        // content below until something forced reflow.
        content.style.height = 'auto';
        const targetPx = content.scrollHeight;
        content.style.height = '0px';

        requestAnimationFrame(function() {
          content.style.transition = 'height 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
          content.style.height = targetPx + 'px';
        });

        setTimeout(function() {
          content.style.height = 'auto';
          content.style.overflow = 'visible';
          content.style.transition = '';
          isAnimating = false;
        }, 400);
      }
    });
  });
}

// Initialize accordions on page load (only for product pages)
document.addEventListener('DOMContentLoaded', function() {
  // Only run on product pages, not collection pages
  const isProductPage = document.body.classList.contains('template-product') || 
                        document.querySelector('.product__info-container') ||
                        document.querySelector('.product-form');
  
  if (isProductPage) {
    // Convert h5 headers to accordions first
    convertH5ToAccordions();
    // Then initialize the accordion functionality with delay
    setTimeout(function() {
      initSimpleFaqAccordions();
    }, 200);
  }
});

// Listen for section loads (only for product pages)
document.addEventListener('shopify:section:load', function() {
  // Only run on product pages, not collection pages
  const isProductPage = document.body.classList.contains('template-product') || 
                        document.querySelector('.product__info-container') ||
                        document.querySelector('.product-form');
  
  if (isProductPage) {
    setTimeout(function() {
      convertH5ToAccordions();
      setTimeout(function() {
        initSimpleFaqAccordions();
      }, 150);
    }, 200);
  }
});

// --- Sprout Product Total Price Calculation ---
document.addEventListener('DOMContentLoaded', function() {
  // Check if it's a Sprout product - more comprehensive detection
  const isCartContext = document.body.classList.contains('template-cart') || document.querySelector('cart-drawer');
  if (isCartContext) return;

  const isSproutProduct = 
    document.querySelector('.product__vendor')?.textContent.trim().includes('Sprout') || 
    document.querySelector('.vendor')?.textContent.trim().includes('Sprout') ||
    document.querySelector('.product-vendor')?.textContent.trim().includes('Sprout') ||
    window.location.pathname.toLowerCase().includes('sprout') ||
    document.querySelector('.product__title')?.textContent.toLowerCase().includes('sprout') ||
    document.querySelector('h1.product__title')?.textContent.toLowerCase().includes('sprout');
  
  if (!isSproutProduct) return;
  
  // Create a container for the total price if it doesn't exist
  let totalPriceContainer = document.querySelector('.sprout-total-price');
  if (!totalPriceContainer) {
    totalPriceContainer = document.createElement('div');
    totalPriceContainer.className = 'sprout-total-price total-price current-total';
    totalPriceContainer.style = 'font-size: 1.4rem; font-weight: 600; margin-bottom: 1rem; text-align: left; color: #333;';
    
    // Add it ABOVE the quantity selector on the left side
    const quantitySelector = document.querySelector('quantity-input') || 
                            document.querySelector('.product-form__quantity');
    if (quantitySelector) {
      // Find the cart-item__quantity div
      const quantityDiv = quantitySelector.closest('.cart-item__quantity');
      if (quantityDiv) {
        // Insert it at the beginning of the cart-item__quantity div
        quantityDiv.insertBefore(totalPriceContainer, quantityDiv.firstChild);
      }
    }
  }
  
  // Initialize prices data structure
  const quantityBreaks = {
    default: null,  // Default price per item
    tiers: []       // Array of {quantity: X, price: Y} objects
  };
  
  // Function to update the total price display
  function updateSproutTotalPrice() {
    // Scope to the product that contains the orichi table if present
    const orichiTable = document.querySelector('.orichi-table');
    let scope = document;
    if (orichiTable) {
      scope = orichiTable.closest('.product') || orichiTable.closest('.product__info-container') || document;
    }

    const quantityInput = scope.querySelector('.quantity__input') || document.querySelector('.quantity__input');
    const quantity = quantityInput ? (parseInt(quantityInput.value, 10) || 1) : 1;
    let pricePerItem = quantityBreaks.default;
    let regularPrice = null;
    let activeRowIndex = 0; // Default to no discount tier (base price)
    
    // Get the regular product price from the page (not from quantity breaks)
    const regularPriceElement = document.querySelector('.price__regular .price-item, .price .price-item--regular, .price-item--regular');
    if (regularPriceElement) {
      const match = regularPriceElement.textContent.replace(/[^\d.,]/g, '').replace(/,/g, '.');
      regularPrice = parseFloat(match) || 0;
    }
    
    // If no regular price found, try to get it from the first price on the page
    if (!regularPrice) {
      const anyPriceElement = document.querySelector('.price__sale .price-item, .price-item--sale, .price .price-item');
      if (anyPriceElement) {
        const match = anyPriceElement.textContent.replace(/[^\d.,]/g, '').replace(/,/g, '.');
        regularPrice = parseFloat(match) || 0;
      }
    }
    
    // Use quantityBreaks.default as fallback for regular price if still not found
    if (!regularPrice && quantityBreaks.default !== null) {
      regularPrice = quantityBreaks.default;
    }
    
    // Find applicable tier pricing
    const sortedTiers = [...quantityBreaks.tiers].sort((a, b) => a.quantity - b.quantity);
    for (let i = sortedTiers.length - 1; i >= 0; i--) {
      const tier = sortedTiers[i];
      if (quantity >= tier.quantity) {
        pricePerItem = tier.price;
        activeRowIndex = i + 1; // +1 because row 0 is base price, tier rows start at 1
        break;
      }
    }
    
    // If no tier data, use fallback price
    if (pricePerItem === null) {
      const priceElement = document.querySelector('.price-per-item--current, .price__regular .price-item, .price__sale .price-item');
      if (priceElement) {
        const match = priceElement.textContent.replace(/[^\d.,]/g, '').replace(/,/g, '.');
        pricePerItem = parseFloat(match) || 0;
        if (regularPrice === null) regularPrice = pricePerItem;
      }
    }
    
    // Highlight the active row in the orichi table
    highlightActiveDiscountRow(activeRowIndex);
    
    const total = quantity * pricePerItem;
    
    // Calculate original total and savings
    let originalTotalHTML = '';
    let savingsHTML = '';
    
    if (regularPrice !== null && regularPrice > 0 && pricePerItem !== null) {
      const originalTotal = quantity * regularPrice;
      
      // Only show original price and savings when there's actually a discount
      if (originalTotal > total && total > 0) {
        originalTotalHTML = ` <span class="original-total" style="text-decoration: line-through; color: #999; margin-left: 0.5rem;">$${originalTotal.toFixed(2)}</span>`;
        
        // Calculate and show savings
        const savings = originalTotal - total;
        if (savings > 0) {
          savingsHTML = `<div class="savings-display" style="font-size: 1.2rem; font-weight: 600; color: #428426; margin-top: 0.4rem;">You Saved: $${savings.toFixed(2)}</div>`;
        }
      }
    }
    
    totalPriceContainer.innerHTML = `$${total.toFixed(2)}${originalTotalHTML}${savingsHTML}`;
    
    // Carefully update Loop widget prices if they exist
    setTimeout(() => {
      // Look for one-time purchase price element
      const loopOnetimePrice = document.querySelector('[id*="loop-widget-purchase-option-price-onetime"]');
      // Look for subscription price elements  
      const loopSubscriptionPrices = document.querySelectorAll('.loop-widget-purchase-option-price:not([id*="onetime"])');
      
      if (loopOnetimePrice) {
        loopOnetimePrice.textContent = `$${total.toFixed(2)}`;
      }
      
      if (loopSubscriptionPrices.length > 0) {
        const subscriptionTotal = total * 0.95; // 5% discount
        loopSubscriptionPrices.forEach(element => {
          element.textContent = `$${subscriptionTotal.toFixed(2)}`;
        });
      }
    }, 100);
  }
  
  // Function to highlight the active discount row
  function highlightActiveDiscountRow(activeRowIndex) {
    // Avoid inline tables which manage their own highlighting
    const orichiTable = document.querySelector('.orichi-table:not(.quantity-discounts-table)');
    if (!orichiTable) return;
    
    // Use only body rows so index 0 => base row, 1..n => tiers
    const rows = orichiTable.querySelectorAll('tbody tr');
    
    // Remove existing highlights
    rows.forEach(row => {
      row.classList.remove('orichi-table__row--active');
      row.classList.remove('orichi-active-row');
    });
    
    // Add highlight to active row when in range
    if (activeRowIndex >= 0 && rows[activeRowIndex]) {
      rows[activeRowIndex].classList.add('orichi-table__row--active');
      rows[activeRowIndex].classList.add('orichi-active-row');
    }
  }
  
  // Function to process the orichi table and extract pricing data
  function processOrichiTable() {
    // Only process the standalone Orichi table, not the inline quantity table
    const orichiTable = document.querySelector('.orichi-table:not(.quantity-discounts-table)');
    if (!orichiTable) return false;
    
    // Clear existing data
    quantityBreaks.tiers = [];
    quantityBreaks.default = null;
    
    // Extract pricing data from table (body rows only)
    const rows = orichiTable.querySelectorAll('tbody tr');
    if (rows.length === 0) return false;
    
    // Get default price from first body row (base row)
    const firstRow = rows[0];
    const firstColumns = firstRow.querySelectorAll('td');
    
    if (firstColumns.length >= 3) {
      const priceText = firstColumns[2].textContent.trim();
      const priceMatch = priceText.match(/\$([0-9]+(\.[0-9]+)?)/);
      if (priceMatch) {
        quantityBreaks.default = parseFloat(priceMatch[1]);
      }
    }
    
    // Get tier prices from remaining body rows
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      const columns = row.querySelectorAll('td');
      
      if (columns.length >= 3) {
        // Extract quantity from first column (e.g., "Buy 3+")
        const qtyText = columns[0].textContent.trim();
        const qtyMatch = qtyText.match(/([0-9]+)\+?/);
        
        // Extract price from third column (e.g., "$29.99 each")
        const priceText = columns[2].textContent.trim();
        const priceMatch = priceText.match(/\$([0-9]+(\.[0-9]+)?)/);
        
        if (qtyMatch && priceMatch) {
          const tier = {
            quantity: parseInt(qtyMatch[1], 10),
            price: parseFloat(priceMatch[1])
          };
          quantityBreaks.tiers.push(tier);
        }
      }
    }
    
    updateSproutTotalPrice();
    return true;
  }
  
  // Set up event listeners for quantity changes
  let qtyUpdateTimer;
  function scheduleQuantityUiUpdate() {
    clearTimeout(qtyUpdateTimer);
    qtyUpdateTimer = setTimeout(function(){
      updateSproutTotalPrice();
    }, 0);
  }
  document.addEventListener('input', function(e) {
    if (e.target.classList.contains('quantity__input')) {
      scheduleQuantityUiUpdate();
    }
  });
  // Also handle +/- buttons explicitly
  document.addEventListener('click', function(e) {
    if (e.target.closest && e.target.closest('.quantity__button')) {
      scheduleQuantityUiUpdate();
    }
  });
  
  // Check for table on initial load and on section loads
  function initOrichiHighlight() {
    if (processOrichiTable()) {
      updateSproutTotalPrice();
    }
  }
  document.addEventListener('DOMContentLoaded', initOrichiHighlight);
  document.addEventListener('shopify:section:load', initOrichiHighlight);

  // Generic highlighter for inline quantity discounts tables using data-quantity attributes
  // Inline quantity discounts table highlighting is handled by sprout-total-pricing.js
  function updateAllDiscountTablesHighlight() { /* no-op */ }
});

// --- Feature Table Mobile Label Formatting ---
document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('.feature-table td[data-label]').forEach(function(td) {
    const original = td.getAttribute('data-label');
    if (!original) return;
    const withBreaks = original.replace(/\s+/g, '\n');
    td.setAttribute('data-label', withBreaks);
  });
});


// --- Blog Post Enhancements: Responsive Tables + FAQ Accordions & Schema ---
// This script runs on blog article pages to
// 1. Add data-label attributes to feature tables so existing responsive CSS/JS can work
// 2. Convert FAQ lists into accordions using existing simple-faq classes
// 3. Inject FAQPage JSON-LD structured data for SEO

document.addEventListener('DOMContentLoaded', function() {
  const isBlogArticle = document.body.classList.contains('template-article') ||
                        document.body.classList.contains('article-template') ||
                        window.location.pathname.toLowerCase().includes('/blogs/');

  if (!isBlogArticle) return;

  /* -----------------------------------------------------------
     1. Responsive Feature Tables
  ----------------------------------------------------------- */
  document.querySelectorAll('table.feature-table').forEach(function(table) {
    // Collect header labels
    let headerCells = Array.from(table.querySelectorAll('thead th'));
    if (headerCells.length === 0) {
      // Fallback: first row cells if thead not present
      const firstRow = table.querySelector('tr');
      if (firstRow) {
        headerCells = Array.from(firstRow.querySelectorAll('th, td'));
      }
    }
    const headers = headerCells.map(function(cell) { return cell.textContent.trim(); });
    if (headers.length === 0) return;

    // Apply data-label to each body row cell
    table.querySelectorAll('tr').forEach(function(row, rowIndex) {
      // Skip header row (index 0) if it contains th elements
      if (rowIndex === 0 && row.querySelectorAll('th').length) return;
      Array.from(row.children).forEach(function(cell, cellIndex) {
        if (!cell.hasAttribute('data-label')) {
          const label = headers[cellIndex] || '';
          if (label) {
            // Preserve mobile line-break format
            cell.setAttribute('data-label', label.replace(/\s+/g, '\n'));
          }
        }
      });
    });
  });

  /* -----------------------------------------------------------
     2. FAQ Conversion to Accordions + Schema Generation
  ----------------------------------------------------------- */
  const faqHeading = Array.from(document.querySelectorAll('h2, h3, h4, h5'))
    .find(function(h) { return /faq|frequently asked questions/i.test(h.textContent); });

  if (faqHeading) {
    let faqList = faqHeading.nextElementSibling;
    if (faqList && (faqList.tagName === 'UL' || faqList.tagName === 'OL')) {
      const accordionContainer = document.createElement('div');
      accordionContainer.className = 'blog-faq-accordions';

      const qaPairs = [];

      Array.from(faqList.children).forEach(function(li) {
        const strong = li.querySelector('strong, b');
        if (!strong) return;
        const question = strong.textContent.trim();
        // Build answer HTML by removing the question element
        const answerHTML = li.innerHTML.replace(strong.outerHTML, '').trim();

        // Create accordion item structure compatible with existing initSimpleFaqAccordions
        const details = document.createElement('details');
        details.className = 'simple-faq__item';

        const summary = document.createElement('summary');
        summary.className = 'simple-faq__summary';
        summary.innerHTML = `\n          <span class="simple-faq__question">${question}</span>\n          <svg class="simple-faq__icon" viewBox="0 0 20 20" fill="currentColor">\n            <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"></path>\n          </svg>`;

        const content = document.createElement('div');
        content.className = 'simple-faq__content rte';
        content.innerHTML = answerHTML;

        details.appendChild(summary);
        details.appendChild(content);
        accordionContainer.appendChild(details);

        qaPairs.push({
          question: question,
          answer: answerHTML.replace(/<[^>]+>/g, '').trim()
        });
      });

      // Replace original list with accordions
      faqHeading.parentNode.insertBefore(accordionContainer, faqHeading.nextSibling);
      faqList.remove();

      // Initialize accordion behaviour
      setTimeout(function() { initSimpleFaqAccordions(accordionContainer); }, 50);

      // Inject FAQPage schema if not already present
      if (qaPairs.length && !document.getElementById('faq-schema')) {
        const schema = {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": qaPairs.map(function(pair) {
            return {
              "@type": "Question",
              "name": pair.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": pair.answer
              }
            };
          })
        };

        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'faq-schema';
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      }
    }
  }

  // Ensure any pre-existing simple-faq items are initialized
  setTimeout(function() { initSimpleFaqAccordions(); }, 100);
});

// --- Global: Add data-label attributes to all tables for mobile fold-down ---
(function setupGlobalMobileTableHeaders() {
  function addTableHeadersToMobileForContainer(root) {
    try {
      const scope = root || document;
      const tables = scope.querySelectorAll('table');
      tables.forEach(function(table) {
        if (table.classList && table.classList.contains('feature-table-table')) return;
        let headers = table.querySelectorAll('thead th');
        if (headers.length === 0) headers = table.querySelectorAll('tr:first-child th');
        if (headers.length === 0) headers = table.querySelectorAll('tr:first-child td');
        if (headers.length === 0) return;

        const headerTexts = Array.from(headers).map(function(cell) {
          const raw = (cell.textContent || '').trim();
          // Replace spaces with newlines to stack labels nicely on mobile
          return raw.replace(/\s+/g, '\n');
        });

        const bodyRows = table.querySelectorAll('tbody tr');
        if (bodyRows.length > 0) {
          bodyRows.forEach(function(row) {
            row.querySelectorAll('td').forEach(function(cell, index) {
              const label = headerTexts[index];
              if (label) cell.setAttribute('data-label', label);
            });
          });
        } else {
          const allRows = table.querySelectorAll('tr');
          const dataRows = Array.from(allRows).slice(1);
          dataRows.forEach(function(row) {
            row.querySelectorAll('td').forEach(function(cell, index) {
              const label = headerTexts[index];
              if (label) cell.setAttribute('data-label', label);
            });
          });
        }
      });
    } catch (e) {
      // no-op
    }
  }

  function initAndBind() {
    addTableHeadersToMobileForContainer(document);

    let resizeTimer;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function() {
        addTableHeadersToMobileForContainer(document);
      }, 200);
    });

    document.addEventListener('shopify:section:load', function(e) {
      addTableHeadersToMobileForContainer(e.target);
    });
    document.addEventListener('shopify:section:select', function(e) {
      addTableHeadersToMobileForContainer(e.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAndBind);
  } else {
    initAndBind();
  }
})();

// --- Accessibility & Performance: ALT fallback and lazy-load images ---
(function addAltAndLazyLoadingFallback() {
  function humanizeFromFilename(src) {
    try {
      if (!src) return '';
      var url = src.split('?')[0];
      var file = decodeURIComponent(url.substring(url.lastIndexOf('/') + 1));
      var base = file.replace(/\.[a-zA-Z0-9]+$/, '');
      base = base.replace(/[\-_]+/g, ' ').trim();
      if (!base) return '';
      return base.charAt(0).toUpperCase() + base.slice(1);
    } catch (e) { return ''; }
  }

  function setAltAndLoading(root) {
    var scope = root || document;
    var images = scope.querySelectorAll('img');
    images.forEach(function(img) {
      var alt = img.getAttribute('alt');
      if (!alt || !alt.trim()) {
        var derived = img.getAttribute('data-alt') || img.getAttribute('aria-label') || img.getAttribute('title') || humanizeFromFilename(img.currentSrc || img.src) || 'Image';
        img.setAttribute('alt', String(derived).trim());
      }
      if (!img.hasAttribute('decoding')) {
        img.setAttribute('decoding', 'async');
      }
      if (!img.hasAttribute('loading')) {
        var inHeader = !!img.closest('header');
        var isLogo = /logo/i.test(img.className || '') || !!img.closest('.header__heading, .site-header__logo');
        if (!inHeader && !isLogo) {
          img.setAttribute('loading', 'lazy');
        }
      }
    });

    var iframes = scope.querySelectorAll('iframe');
    iframes.forEach(function(frame) {
      var inHeader = !!frame.closest('header');
      if (!frame.hasAttribute('loading') && !inHeader) {
        frame.setAttribute('loading', 'lazy');
      }
    });
  }

  function init() {
    setAltAndLoading(document);
    document.addEventListener('shopify:section:load', function(e) { setAltAndLoading(e.target); });
    document.addEventListener('shopify:section:select', function(e) { setAltAndLoading(e.target); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// --- Preserve last page and re-open cart drawer after returning from Checkout ---
(function handleCheckoutReturnCartDrawer() {
  var STORAGE_RETURN_URL_KEY = 'gobCartReturnUrl';
  var STORAGE_SHOULD_OPEN_KEY = 'gobCartShouldOpenDrawer';

  function withQuery(urlString, key, value) {
    try {
      var url = new URL(urlString, window.location.origin);
      url.searchParams.set(key, value);
      return url.toString();
    } catch (e) {
      return urlString;
    }
  }

  function saveReturnContextBeforeCheckout() {
    try {
      // Only save if current page is not the cart page itself
      var path = window.location.pathname || '';
      if (path.indexOf('/cart') === 0) return;
      sessionStorage.setItem(STORAGE_RETURN_URL_KEY, window.location.href);
      sessionStorage.setItem(STORAGE_SHOULD_OPEN_KEY, '1');
    } catch (e) {}
  }

  function attachCheckoutListeners() {
    try {
      var drawerCheckoutBtn = document.getElementById('CartDrawer-Checkout');
      if (drawerCheckoutBtn) {
        drawerCheckoutBtn.addEventListener('click', saveReturnContextBeforeCheckout, { capture: true });
      }

      var drawerForm = document.getElementById('CartDrawer-Form');
      if (drawerForm) {
        drawerForm.addEventListener('submit', function (e) {
          var submitter = (e.submitter && e.submitter.name) || '';
          if (submitter === 'checkout' || (e.submitter && e.submitter.id === 'CartDrawer-Checkout')) {
            saveReturnContextBeforeCheckout();
          }
        }, { capture: true });
      }
    } catch (e) {}
  }

  function maybeRedirectFromCart() {
    try {
      var shouldOpen = sessionStorage.getItem(STORAGE_SHOULD_OPEN_KEY) === '1';
      var savedUrl = sessionStorage.getItem(STORAGE_RETURN_URL_KEY);
      var isCartPage = (window.location.pathname || '').indexOf('/cart') === 0;

      if (!isCartPage || !shouldOpen || !savedUrl) return;

      // Avoid redirect loops if saved URL points to cart
      try {
        var parsed = new URL(savedUrl, window.location.origin);
        if ((parsed.pathname || '').indexOf('/cart') === 0) return;
      } catch (e) {}

      // Clear flags before redirect to prevent loops
      sessionStorage.removeItem(STORAGE_SHOULD_OPEN_KEY);
      sessionStorage.removeItem(STORAGE_RETURN_URL_KEY);

      var target = withQuery(savedUrl, 'open_cart_drawer', '1');
      window.location.replace(target);
    } catch (e) {}
  }

  function maybeOpenDrawerFromParam() {
    try {
      var params = new URLSearchParams(window.location.search || '');
      if (!params.has('open_cart_drawer')) return;
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        // Defer to ensure custom element is upgraded
        setTimeout(function () { drawer.open(); }, 0);
      }
    } catch (e) {}
  }

  function init() {
    attachCheckoutListeners();
    maybeRedirectFromCart();
    maybeOpenDrawerFromParam();

    // Handle bfcache restores
    window.addEventListener('pageshow', function () {
      maybeRedirectFromCart();
      maybeOpenDrawerFromParam();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  if (typeof IntersectionObserver === 'undefined') {
    animationTriggerElements.forEach((element) => {
      element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.classList.add(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => {
    if (element.dataset.scrollTriggerObserved === 'true') return;
    element.dataset.scrollTriggerObserved = 'true';
    observer.observe(element);
  });
}

function revealVisibleScrollTriggers(rootEl = document, revealAll = false) {
  const triggers = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (triggers.length === 0) return;

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  window.requestAnimationFrame(() => {
    triggers.forEach((trigger) => {
      if (!trigger.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) return;

      const rect = trigger.getBoundingClientRect();
      const isVisible = revealAll || (rect.top <= viewportHeight && rect.bottom >= 0);

      if (isVisible) {
        trigger.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        trigger.classList.add(SCROLL_ANIMATION_CANCEL_CLASSNAME);
      }
    });
  });
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (typeof IntersectionObserver === 'undefined') return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
  window.setTimeout(() => revealVisibleScrollTriggers(), 400);
});

window.addEventListener('load', () => {
  revealVisibleScrollTriggers(document, true);
  window.setTimeout(() => revealVisibleScrollTriggers(document, true), 1200);
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeScrollAnimationTrigger(event.target, true);
    window.setTimeout(() => revealVisibleScrollTriggers(event.target, true), 200);
  });
  document.addEventListener('shopify:section:reorder', () => {
    initializeScrollAnimationTrigger(document, true);
    window.setTimeout(() => revealVisibleScrollTriggers(document, true), 200);
  });
}
